import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import Moment from "react-moment";
import "moment/locale/pt-br";

export default function VotingNotifications({ postId, isSubscribed, onSubscriptionChange }) {
  const { user } = useSelector((state) => ({ ...state }));
  const [notifications, setNotifications] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loading, setLoading] = useState(false);

  // Buscar notificações
  const fetchNotifications = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/voting/${postId}/notifications`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setNotifications(data.notifications || []);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  // Buscar campanhas
  const fetchCampaigns = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/voting/${postId}/campaigns`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setCampaigns(data.campaigns || []);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    }
  };

  // Seguir/deixar de seguir votação
  const handleSubscription = async () => {
    try {
      setLoading(true);
      if (isSubscribed) {
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_URL}/voting/${postId}/subscribe`,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/voting/${postId}/subscribe`,
          {},
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
      }
      onSubscriptionChange(!isSubscribed);
      setLoading(false);
    } catch (error) {
      console.error("Error toggling subscription:", error);
      setLoading(false);
    }
  };

  // Curtir campanha
  const handleLikeCampaign = async (campaignId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/voting/${postId}/campaigns/${campaignId}/like`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      fetchCampaigns(); // Atualizar lista
    } catch (error) {
      console.error("Error liking campaign:", error);
    }
  };

  useEffect(() => {
    if (isSubscribed && showNotifications) {
      fetchNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubscribed, showNotifications]);

  useEffect(() => {
    if (showCampaigns) {
      fetchCampaigns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCampaigns]);

  return (
    <div className="voting_notifications_container">
      {/* Botão de inscrição */}
      <button
        className={`voting_subscribe_btn ${isSubscribed ? "subscribed" : ""}`}
        onClick={handleSubscription}
        disabled={loading}
      >
        {isSubscribed ? "🔔 Seguindo" : "🔕 Seguir Votação"}
      </button>

      {/* Botão de campanhas */}
      <button
        className="voting_campaigns_btn"
        onClick={() => setShowCampaigns(!showCampaigns)}
      >
        📢 Campanhas ({campaigns.length})
      </button>

      {/* Botão de notificações */}
      {isSubscribed && (
        <button
          className="voting_notifications_btn"
          onClick={() => setShowNotifications(!showNotifications)}
        >
          📩 Notificações ({notifications.length})
        </button>
      )}

      {/* Lista de campanhas */}
      {showCampaigns && (
        <div className="voting_campaigns_list">
          <h3>Campanhas</h3>
          {campaigns.length === 0 ? (
            <p className="no_campaigns">Nenhuma campanha ainda</p>
          ) : (
            campaigns.map((campaign) => (
              <div key={campaign._id} className="campaign_card">
                <div className="campaign_header">
                  {campaign.isOfficial && (
                    <span className="official_badge">✓ Oficial</span>
                  )}
                  <h4>{campaign.title}</h4>
                </div>
                <p className="campaign_description">{campaign.description}</p>
                {campaign.content && (
                  <div className="campaign_content">{campaign.content}</div>
                )}
                {campaign.images && campaign.images.length > 0 && (
                  <div className="campaign_images">
                    {campaign.images.map((img, idx) => (
                      <img key={idx} src={img} alt="Campaign" />
                    ))}
                  </div>
                )}
                <div className="campaign_footer">
                  <div className="campaign_meta">
                    <span className="campaign_author">
                      Por {campaign.createdBy?.first_name || "Anônimo"}
                    </span>
                    <span className="campaign_date">
                      <Moment fromNow locale="pt-br">
                        {campaign.createdAt}
                      </Moment>
                    </span>
                  </div>
                  <div className="campaign_actions">
                    <button
                      className="campaign_like_btn"
                      onClick={() => handleLikeCampaign(campaign._id)}
                    >
                      {campaign.likes?.some((l) => l === user._id) ? "❤️" : "🤍"}{" "}
                      {campaign.likes?.length || 0}
                    </button>
                    <span className="campaign_views">
                      👁️ {campaign.views || 0}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Lista de notificações */}
      {showNotifications && (
        <div className="voting_notifications_list">
          <h3>Notificações</h3>
          {notifications.length === 0 ? (
            <p className="no_notifications">Nenhuma notificação ainda</p>
          ) : (
            notifications.map((notif, idx) => (
              <div key={idx} className="notification_card">
                <div className="notification_type_badge">
                  {notif.type === "campaign_posted" && "📢"}
                  {notif.type === "voting_reminder" && "⏰"}
                  {notif.type === "voting_ended" && "🏁"}
                  {notif.type === "voting_results" && "📊"}
                </div>
                <div className="notification_content">
                  <p>{notif.message}</p>
                  <span className="notification_date">
                    <Moment fromNow locale="pt-br">
                      {notif.sentAt}
                    </Moment>
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
